import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert } from '@/common/components/Alert/Alert';
import { BulletPoints } from '@/common/components/BulletPoints/BulletPoints';
import { PaymentMethodsField } from '@/common/components/Fields/PaymentMethodsField/PaymentMethodsField';
import { Grid } from '@/common/components/Grid/Grid';
import { Stack } from '@/common/components/Stack/Stack';
import { Translate } from '@/common/components/Translate/Translate';
import { ROUTES } from '@/common/constants/routes';
import { useGetMealPaymentStrategy } from '@/common/containers/Dashboard/MealVoucher/MealVoucher.utils';
import { AnalyticsService } from '@/common/integrations/analytics/analytics.service';
import { useGetCreditsAutoRechargeConfig } from '@/common/integrations/menhir/employee/credits/credits.hooks';
import { useActivateMealSinglePayment } from '@/common/integrations/menhir/employee/meal/meal.hooks';
import { TMealConfiguration } from '@/common/integrations/menhir/employee/meal/meal.types';
import { useGetPaymentMethods } from '@/common/integrations/menhir/employee/payments/payments.hooks';
import { IEmployeePaymentMethod } from '@/common/integrations/menhir/employee/payments/payments.types';
import { useGetPockets } from '@/common/integrations/menhir/employee/pockets/pockets.hooks';
import { FlowLayout } from '@/common/layouts/FlowLayout/FlowLayout';
import { useFlowLayout } from '@/common/layouts/FlowLayout/FlowLayout.context';
import { tArray } from '@/common/utils/i18n';
import { notify } from '@/common/utils/toast/toast';
import { usePageViewTracking } from '@/web/hooks/usePageViewTracking';
import { MoneyUtils, defaultMoney } from '@coverflex-tech/hypernova-utils';
import { useRouter } from 'expo-router';

export type TAutomaticPaymentConfirmStepProps = {
  currentConfig?: TMealConfiguration['singleTransaction'];
};

export function AutomaticPaymentConfirmStep({
  currentConfig,
}: TAutomaticPaymentConfirmStepProps) {
  const router = useRouter();
  const { t } = useTranslation();
  const { dismiss } = useFlowLayout();
  const { paymentMethods = [] } = useGetPaymentMethods();
  const { creditsPocket } = useGetPockets();
  const { mutateAsync, isLoading } = useActivateMealSinglePayment();
  const { autoRechargeConfig, error: autoRechargeError } =
    useGetCreditsAutoRechargeConfig();

  const mealPaymentStrategy = useGetMealPaymentStrategy();
  usePageViewTracking('single_transaction_link_card_page', {
    current_strategy: mealPaymentStrategy,
  });

  const [paymentMethod, setPaymentMethod] = useState<
    IEmployeePaymentMethod | undefined
  >(() => {
    return (
      paymentMethods.find(
        (method) => method.id === currentConfig?.paymentMethod.id
      ) ?? paymentMethods[0]
    );
  });

  const [hasChanged, setHasChanged] = useState(false);

  const initialPaymentMethodId = currentConfig?.paymentMethod.id;

  const handlePaymentMethodChange = (
    method: IEmployeePaymentMethod | undefined
  ) => {
    setPaymentMethod(method);
    if (isEditing && method) {
      setHasChanged(method.id !== initialPaymentMethodId);
    }
  };

  const isEditing = !!currentConfig;

  const onConfirm = async () => {
    if (!paymentMethod) {
      return;
    }

    AnalyticsService.trackCustomEvent(
      'single_transaction_link_card_page_link_card_button_clicked',
      {
        current_strategy: mealPaymentStrategy,
      }
    );

    try {
      await mutateAsync(paymentMethod.id);
      notify({
        type: 'success',
        message: isEditing
          ? t('dashboard.automatic_payment.setup.step.confirm.success_edit')
          : t('dashboard.automatic_payment.setup.step.confirm.success'),
      });
      router.dismissTo(ROUTES.MEAL_VOUCHER);
    } catch (error) {
      notify({
        type: 'error',
        message: t('dashboard.automatic_payment.setup.step.confirm.error'),
      });
    }
  };

  const hasAutoRecharge = !!autoRechargeConfig && !autoRechargeError;
  const whatsNextPoints = tArray<string>(
    'dashboard.automatic_payment.setup.step.confirm.whats_next.bullets'
  ).concat(
    hasAutoRecharge
      ? [
          'dashboard.automatic_payment.setup.step.confirm.whats_next.bullets.auto_recharge',
        ]
      : []
  );

  const creditsBalance = creditsPocket?.balance ?? defaultMoney;

  const tertiaryAction = isEditing
    ? {
        children: t(
          'dashboard.automatic_payment.setup.step.confirm.tertiary_action'
        ),
        onPress: () => {
          AnalyticsService.trackCustomEvent(
            'single_transaction_link_card_page_change_payment_method_button_clicked',
            {
              current_strategy: mealPaymentStrategy,
            }
          );
          dismiss();
          router.navigate(ROUTES.MEAL_VOUCHER_PAYMENT);
        },
      }
    : undefined;

  return (
    <FlowLayout.Content
      canGoBack={false}
      primaryAction={{
        children: isEditing
          ? t(
              'dashboard.automatic_payment.setup.step.confirm.primary_action_edit'
            )
          : t('dashboard.automatic_payment.setup.step.confirm.primary_action'),
        onPress: onConfirm,
        disabled: !paymentMethod || (isEditing && !hasChanged),
        loading: isLoading,
      }}
      tertiaryAction={tertiaryAction}
    >
      <Grid columnBasis="basis-[26rem]" gap="md">
        <PaymentMethodsField
          methods={paymentMethods}
          selectedMethod={paymentMethod}
          onSelect={handlePaymentMethodChange}
          labelKey="general.labels.card"
        />
      </Grid>
      <Stack gap="md">
        <BulletPoints
          title={t(
            'dashboard.automatic_payment.setup.step.confirm.whats_next.title'
          )}
          points={whatsNextPoints}
          color="neutral20"
        />
        {creditsBalance.amount > 0 && (
          <Alert type="info">
            <Translate
              i18nKey="dashboard.automatic_payment.setup.step.confirm.credits_alert"
              values={{
                amount: MoneyUtils.moneyToString(creditsBalance),
              }}
            />
          </Alert>
        )}
      </Stack>
    </FlowLayout.Content>
  );
}
